/**
 * W76 Block 2 — Crypto-Macro-Hero-Card.
 *
 * Aggregiert die 3 wichtigsten Krypto-Macro-Datenpunkte fuer Position-Trader:
 *   - Halving-Cycle-Position (Tag X von 1458, Phase, Progress)
 *   - Fear-and-Greed-Index (Wert + Klartext, Contrarian-Hinweis)
 *   - Funding-Rate Perpetual (nur fuer BTC/ETH/SOL... gemappte Coins)
 *
 * Audit Agent F HIGH: war Top-Empfehlung fuer Krypto-Hero-Metric.
 *
 * Wird angezeigt:
 *   - Watchlist Krypto-Tab als globaler Header (sicht-Symbol-unabhaengig)
 *   - Symbol-Detail fuer asset_class='crypto'
 */

import { useDataState } from '@/hooks/use-data-state';
import { computeHalvingCycle } from '@/lib/halving-cycle';
import { fetchFearGreedIndex } from '@/lib/fear-greed';
import { fetchFundingRate } from '@/lib/funding-rates';
import { fetchCryptoFundamentals, fetchCryptoMarketGlobal } from '@/lib/crypto-fundamentals';
// W93-Krypto — Boden-Score + Fib-Leiter aus v5_crypto_boden (Server-Cron 18:15)
import {
  fetchCryptoBodenAll,
  bodenBadgeClass,
  sortFibStatesForDisplay,
} from '@/lib/crypto-boden-read';
// W79.8 — Crypto-Marktphase aus W75.6 jetzt UI-sichtbar
import { computeCryptoMarketPhase } from '@/lib/v51/market-phase';
import { CARD, CARD_HEADER } from '@/lib/design-tokens';
import { CardInfoIcon } from '@/components/ui/CardInfoIcon';
import { cn } from '@/lib/utils';
import { Activity, Bitcoin, Gauge } from 'lucide-react';

interface Props {
  /** Falls gesetzt: Funding-Rate fuer dieses Symbol mit anzeigen (BTC/USD etc.) */
  symbol?: string;
}

export function CryptoMacroHeroCard({ symbol }: Props): JSX.Element {
  const cycle = computeHalvingCycle();

  const fearGreed = useDataState({
    queryKey: ['crypto-fear-greed'],
    queryFn: (signal) => fetchFearGreedIndex(signal),
    ttlSeconds: 30 * 60,
  });

  // W88-P8 (2026-05-27) — Wenn kein Symbol explizit gesetzt (Watchlist-Macro-
  // Kontext), nutze BTC/USD als Default. BTC-Funding ist der Standard-Markt-
  // Indikator für Krypto-Sentiment ohne dass der User ein Symbol auswählen muss.
  const fundingSymbol = symbol ?? 'BTC/USD';
  const funding = useDataState({
    queryKey: ['crypto-funding', fundingSymbol],
    queryFn: (signal) => fetchFundingRate(fundingSymbol, signal),
    ttlSeconds: 15 * 60,
  });

  // W79.8 — BTC-Fundamentals + Global fuer computeCryptoMarketPhase
  const btcFund = useDataState({
    queryKey: ['crypto-fund-for-phase', 'BTC/USD'],
    queryFn: (signal) => fetchCryptoFundamentals('BTC/USD', signal),
    ttlSeconds: 30 * 60,
  });
  const cryptoGlobal = useDataState({
    queryKey: ['crypto-global-for-phase'],
    queryFn: (signal) => fetchCryptoMarketGlobal(signal),
    ttlSeconds: 30 * 60,
  });

  // W93-Krypto — Boden-Score-Map (geteilter Cache mit SymbolScoreCell)
  const bodenAll = useDataState({
    queryKey: ['crypto-boden-all'],
    queryFn: (signal) => fetchCryptoBodenAll(signal),
    ttlSeconds: 30 * 60,
    isEmpty: (m) => !m || m.size === 0,
  });
  const boden = bodenAll.state.data?.get(fundingSymbol.toUpperCase()) ?? null;

  const fg = fearGreed.state.data;
  const fr = funding.state.data;

  // W79.8 — Crypto-Marktphase ableiten
  const cryptoPhase = (() => {
    const btc = btcFund.state.data;
    if (!btc) return null;
    return computeCryptoMarketPhase({
      btc_pct_above_ma200: null, // benoetigt MA200 — TODO mit OHLCV
      btc_dominance_pct: cryptoGlobal.state.data?.btc_dominance_pct ?? null,
      btc_drawdown_from_ath_pct: btc.ath_change_pct ?? null,
      fear_greed_index: fg?.value ?? null,
    });
  })();

  return (
    <section className={cn(CARD, 'space-y-3')}>
      <header className="flex items-center justify-between">
        <h3 className={CARD_HEADER}>
          <Bitcoin className="size-4" /> Krypto-Macro
          <CardInfoIcon hint="Halving-Cycle = wo im 4-Jahres-BTC-Zyklus stehen wir. Fear-Greed-Index (alternative.me) als Contrarian-Signal. Funding-Rate = Perpetual-Sentiment, >+0,05 % = Long-Crowded (Squeeze-Risiko), <−0,02 % = Short-Crowded (Bounce-Setup). Krypto-Markt-Phase aus BTC-Dominanz + Drawdown + Fear-Greed (entkoppelt vom SPY-Makro). Boden-Score = Akkumulations-Nähe (200W-MA, Zyklus-Fibs, Halving-Uhr, Mayer, Sentiment) — hoch heißt historisch gute Kaufzone, nicht das Tief ist da." />
        </h3>
        <span className="text-[10px] text-zinc-500 uppercase tracking-wide">
          Cycle · Sentiment · Funding
        </span>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Halving-Cycle */}
        <div className="rounded-md border border-zinc-800/80 bg-zinc-900/30 p-3 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">
            Halving-Cycle
          </div>
          <div className="text-base font-mono font-semibold text-zinc-100">
            Tag {cycle.days_since_halving} / 1458
          </div>
          <div className="text-[11px] text-zinc-400 leading-snug">{cycle.label}</div>
          <div className="mt-1 h-1 w-full rounded-full bg-zinc-800 overflow-hidden">
            <div
              className="h-full bg-cockpit-accent transition-all"
              style={{ width: `${(cycle.progress_pct * 100).toFixed(1)}%` }}
            />
          </div>
        </div>

        {/* Fear-Greed */}
        <div className="rounded-md border border-zinc-800/80 bg-zinc-900/30 p-3 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wide text-zinc-500 flex items-center gap-1">
            <Gauge className="size-3" /> Fear & Greed
          </div>
          {fg === null || fg === undefined ? (
            <div className="text-xs text-zinc-500">{fearGreed.state.status === 'loading' ? 'Lade …' : 'Nicht verfügbar'}</div>
          ) : (
            <>
              <div
                className={cn(
                  'text-base font-mono font-semibold',
                  fg.value <= 25
                    ? 'text-emerald-300'
                    : fg.value >= 75
                      ? 'text-red-300'
                      : 'text-zinc-100'
                )}
              >
                {fg.value} · {fg.phase_de}
              </div>
              <div className="text-[11px] text-zinc-400 leading-snug">{fg.hint_de}</div>
            </>
          )}
        </div>

        {/* Funding-Rate */}
        <div className="rounded-md border border-zinc-800/80 bg-zinc-900/30 p-3 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wide text-zinc-500 flex items-center gap-1">
            <Activity className="size-3" /> Funding-Rate{!symbol ? ' (BTC)' : ''}
          </div>
          {fr === null || fr === undefined ? (
            <div className="text-xs text-zinc-500">{funding.state.status === 'loading' ? 'Lade …' : 'Nicht verfügbar'}</div>
          ) : (
            <>
              <div
                className={cn(
                  'text-base font-mono font-semibold',
                  fr.funding_rate_pct > 0.02
                    ? 'text-red-300'
                    : fr.funding_rate_pct < -0.01
                      ? 'text-emerald-300'
                      : 'text-zinc-100'
                )}
              >
                {fr.funding_rate_pct >= 0 ? '+' : ''}
                {fr.funding_rate_pct.toFixed(4)} %
              </div>
              <div className="text-[11px] text-zinc-400 leading-snug">
                {fr.phase_de} — {fr.hint_de}
              </div>
            </>
          )}
        </div>
      </div>

      {/* W79.8 — Crypto-Marktphase aus W75.6 (BTC-Dominance + DD + Fear-Greed) */}
      {cryptoPhase && (
        <div className="rounded-md border border-cockpit-accent/30 bg-cockpit-accent/5 px-3 py-2 text-[11px]">
          <div className="font-semibold text-cockpit-accent uppercase tracking-wide text-[10px] mb-0.5">
            Crypto-Markt-Phase: {cryptoPhase.phase.replace(/_/g, ' ')}
          </div>
          <div className="text-zinc-300 leading-snug">{cryptoPhase.summary}</div>
          <div className="text-zinc-500 mt-0.5">
            Empfehlung: {cryptoPhase.preferred_action.replace(/_/g, ' ')}
          </div>
        </div>
      )}

      {/* W93-Krypto — Boden-Score + Fib-Leiter: "Wo im Bärenmarkt stehen wir,
          und welche Reaktionszonen sind JETZT aktiv?" (User-Auftrag: Fib-Levels
          als Wahrscheinlichkeits-Hinweise, nicht als Orakel.) */}
      {boden && (
        <div className="rounded-md border border-zinc-800/80 bg-zinc-900/30 px-3 py-2 space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">
              Zyklus-Boden-Score ({fundingSymbol.replace('/USD', '')})
            </div>
            <span
              className={cn(
                'inline-flex items-center rounded border px-1.5 py-px text-[11px] font-mono',
                bodenBadgeClass(boden.boden_score, boden.falling_knife)
              )}
            >
              {boden.falling_knife ? '⚠ ' : ''}Boden {boden.boden_score}
              {boden.t1_ready ? ' · Tranche 1 möglich' : ''}
            </span>
          </div>
          <div className="text-[11px] text-zinc-300 leading-snug">{boden.summary}</div>
          {boden.fib_states.length > 0 && (
            <div className="space-y-0.5 border-t border-zinc-800 pt-1">
              {sortFibStatesForDisplay(boden.fib_states).slice(0, 3).map((f) => (
                <div key={`${f.anchor}-${f.ratio}`} className="text-[11px] text-zinc-400 leading-snug">
                  {f.hint_de}
                </div>
              ))}
            </div>
          )}
          {boden.invalidation && (
            <div className="text-[10px] text-amber-300/80 leading-snug">{boden.invalidation}</div>
          )}
        </div>
      )}

    </section>
  );
}
